import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Period } from '../../../../../../types/Exams';

type TimePickerWithLimitedTimeProps = {
  setDateTime: (date: Date) => void;
  start?: number; // business days offset for the single picker
  disabledPeriods?: Period[];
  isRange?: boolean; // if true => behaves as the "To" picker
  rangeStart?: Date | null; // "From" value for the "To" picker
  stepMinutes?: number; // default 15 minutes
  workingHours?: { start: string; end: string }; // 'HH:mm' (default 09:30–17:30)
  initialSelection?: Date | null;
  preferExactSelection?: boolean;
  refreshKey?: number;
};

function parseHM(hm: string): [number, number] {
  const [h, m] = hm.split(':').map(Number);
  return [h || 0, m || 0];
}

function addBusinessDays(from: Date, days: number) {
  console.log('addBusinessDays from', from);
  console.log('addBusinessDays days', days);

  const d = new Date(from);
  while (days > 0) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days--;
  }
  console.log('addBusinessDays d', d);
  return d;
}

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function TimePickerWithLimitedTime({
  setDateTime,
  start = 0,
  disabledPeriods = [],
  isRange = false,
  rangeStart = null,
  stepMinutes = 15,
  workingHours = { start: '09:30', end: '17:30' },
  initialSelection = null,
  preferExactSelection = false,
  refreshKey,
}: TimePickerWithLimitedTimeProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Date>(new Date());

  const [whStartH, whStartM] = parseHM(workingHours.start);
  const [whEndH, whEndM] = parseHM(workingHours.end);

  // Baseline minimum date:
  // - Single: business-days offset from "today"
  // - To picker: day of the given rangeStart
  const minSelectableDate = useMemo(() => {
    if (isRange && rangeStart) return startOfDay(rangeStart);
    return addBusinessDays(new Date(), start);
  }, [isRange, rangeStart, start]);

  // Normalize disabled periods to Date
  const periods = useMemo(() => {
    const toDate = (v: Date | string) => (v instanceof Date ? v : new Date(v));
    return disabledPeriods
      .map((p) => ({ start: toDate(p.start), end: toDate(p.end) }))
      .filter((p) => !isNaN(p.start.getTime()) && !isNaN(p.end.getTime()) && p.start < p.end)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [disabledPeriods]);

  const overlaps = (t: Date, p: { start: Date; end: Date }) =>
    t.getTime() >= p.start.getTime() && t.getTime() < p.end.getTime();

  const isWeekday = (date: Date) => {
    const dow = date.getDay();
    return dow !== 0 && dow !== 6;
  };
  useEffect(() => {
    if (!selected) return;
    if (preferExactSelection) return;
    if (!isValidSelection(selected)) {
      const sameDayEarliest = firstAvailableOnDate(selected);
      const fallback = sameDayEarliest ?? findFirstAvailable();
      setSelected(fallback);
      setDateTime(fallback);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods, preferExactSelection]);

  const dayHasFreeSlot = (date: Date) => {
    const from = new Date(date);
    from.setHours(whStartH, whStartM, 0, 0);

    // If To picker & same day as rangeStart, the earliest allowed moment is rangeStart itself.
    const dayMin = isRange && rangeStart && sameDay(date, rangeStart) ? new Date(rangeStart) : from;

    const to = new Date(date);
    to.setHours(whEndH, whEndM, 0, 0);

    for (let t = dayMin.getTime(); t <= to.getTime(); t += stepMinutes * 60_000) {
      const candidate = new Date(t);
      if (!periods.some((p) => overlaps(candidate, p))) {
        return true;
      }
    }
    return false;
  };

  const filterDate = (date: Date) => {
    if (date < startOfDay(minSelectableDate)) return false;
    return isWeekday(date) && dayHasFreeSlot(date);
  };

  // Time-level blocking:
  const filterTime = (time: Date) => {
    const candidate = new Date(selected);
    candidate.setHours(time.getHours(), time.getMinutes(), 0, 0);
    const now = new Date();

    // 🔒 disallow times earlier than "now" when selecting today
    if (sameDay(candidate, now) && candidate < now) return false;
    if (!isRange && sameDay(candidate, minSelectableDate) && candidate < minSelectableDate) {
      return false;
    }
    // Block disabled periods
    if (periods.some((p) => overlaps(candidate, p))) return false;

    // In "To" picker, when same day as rangeStart, do not allow times before rangeStart
    if (isRange && rangeStart && sameDay(candidate, rangeStart) && candidate < rangeStart) {
      return false;
    }
    return true;
  };

  const ceilToStep = (d: Date) => {
    const x = new Date(d);
    const mins = x.getMinutes();
    const rem = mins % stepMinutes;
    if (rem !== 0) x.setMinutes(mins + (stepMinutes - rem), 0, 0);
    else x.setSeconds(0, 0);
    return x;
  };

  const firstSlotOnDay = (day: Date) => {
    const d = new Date(day);
    d.setHours(whStartH, whStartM, 0, 0);
    return d;
  };

  const lastSlotOnDay = (day: Date) => {
    const d = new Date(day);
    d.setHours(whEndH, whEndM, 0, 0);
    return d;
  };

  const nextBusinessDay = (d: Date) => {
    const n = new Date(d);
    n.setDate(n.getDate() + 1);
    while (!isWeekday(n)) n.setDate(n.getDate() + 1);
    n.setHours(whStartH, whStartM, 0, 0);
    return n;
  };

  const isBlocked = (d: Date) => periods.some((p) => overlaps(d, p));

  const withinWorkingWindow = (d: Date) => {
    const start = firstSlotOnDay(d);
    const end = lastSlotOnDay(d);
    return d >= start && d <= end;
  };

  const isValidSelection = (d: Date) => {
    if (!isWeekday(d)) return false;
    if (!withinWorkingWindow(d)) return false;

    // Respect "To" picker constraint if you use range mode
    if (isRange && rangeStart && sameDay(d, rangeStart) && d < rangeStart) return false;

    // Respect baseline min (business days offset)
    if (!isRange && d < minSelectableDate) return false;

    // Finally, must not be blocked
    return !isBlocked(d);
  };
  // const firstAvailableOnDate = (baseDay: Date): Date | null => {
  //   if (!isWeekday(baseDay)) return null;

  //   let timeStart = firstSlotOnDay(baseDay);

  //   // For "To" picker, clamp to rangeStart if same day
  //   if (isRange && rangeStart && sameDay(baseDay, rangeStart)) {
  //     const clamp = new Date(Math.max(timeStart.getTime(), rangeStart.getTime()));
  //     timeStart = ceilToStep(clamp);
  //   } else {
  //     timeStart = ceilToStep(timeStart);
  //   }

  //   const timeEnd = lastSlotOnDay(baseDay);

  //   for (let t = timeStart.getTime(); t <= timeEnd.getTime(); t += stepMinutes * 60_000) {
  //     const candidate = new Date(t);
  //     if (!periods.some((p) => overlaps(candidate, p))) return candidate;
  //   }
  //   return null;
  // };
  const firstAvailableOnDate = (baseDay: Date): Date | null => {
    if (!isWeekday(baseDay)) return null;

    const now = new Date();
    const dayStart = firstSlotOnDay(baseDay);
    const dayEnd = lastSlotOnDay(baseDay);

    let timeStart: Date;

    if (isRange && rangeStart && sameDay(baseDay, rangeStart)) {
      // “To” picker: can’t be before rangeStart on the same day
      const clamp = new Date(Math.max(dayStart.getTime(), rangeStart.getTime()));
      timeStart = ceilToStep(clamp);
    } else if (sameDay(baseDay, now)) {
      // Today: start from now (but not before working start)
      const clamp = new Date(Math.max(dayStart.getTime(), now.getTime()));
      timeStart = ceilToStep(clamp);
    } else {
      // Other days: start from the day’s working start
      timeStart = ceilToStep(dayStart);
    }

    for (let t = timeStart.getTime(); t <= dayEnd.getTime(); t += stepMinutes * 60_000) {
      const candidate = new Date(t);
      if (!periods.some((p) => overlaps(candidate, p))) return candidate;
    }
    return null;
  };

  // Scan forward from the first eligible day, **starting at that day's working start**,
  // to find the earliest free slot.
  const findFirstAvailable = (): Date => {
    let day = startOfDay(isRange && rangeStart ? rangeStart : minSelectableDate);
    // Look up to 180 business days
    for (let i = 0; i < 180; i++) {
      const candidate = firstAvailableOnDate(day);
      if (candidate) return candidate;
      day = nextBusinessDay(day);
    }
    // Fallback: first slot on last checked day
    return firstSlotOnDay(day);
  };

  // Initialize selection (and re-run when constraints change)
  useEffect(
    () => {
      let next: Date;
      if (preferExactSelection && initialSelection) {
        next = new Date(initialSelection); // <-- show “wrong” pick exactly
      } else if (isRange && rangeStart) {
        next = new Date(rangeStart);
      } else {
        next = firstAvailableOnDate(minSelectableDate) ?? findFirstAvailable(); // your existing logic
      }

      setSelected(next);
      setDateTime(next);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      preferExactSelection,
      initialSelection?.getTime?.(), // re-init when value changes
      isRange,
      rangeStart?.getTime?.(),
      refreshKey,
    ], // allow parent to force re-init
    // if you want re-init also when periods change:
    // JSON.stringify(disabledPeriods),
  );

  // If rangeStart changes and the current selection is earlier → clamp to rangeStart
  useEffect(() => {
    if (isRange && rangeStart) {
      if (selected < rangeStart) {
        setSelected(rangeStart);
        setDateTime(rangeStart);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRange, rangeStart]);

  // When a date is picked, snap to earliest free slot **on that date**
  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    const d = new Date(date);

    const dayChanged =
      !selected ||
      selected.getFullYear() !== d.getFullYear() ||
      selected.getMonth() !== d.getMonth() ||
      selected.getDate() !== d.getDate();

    if (dayChanged) {
      // user clicked a different calendar day → snap to earliest free time on that day
      const earliest = firstAvailableOnDate(d);
      const chosen = earliest ?? d; // safety fallback
      setSelected(chosen);
      setDateTime(chosen);
    } else {
      // user changed the time on the same day → use exactly what they picked
      setSelected(d);
      setDateTime(d);
      setOpen(false);
    }
  };

  // const handleDateChange = (date: Date | null) => {
  //   if (!date) return;
  //   setSelected(date);
  //   setDateTime(date);
  // };
  // Time bounds for the currently focused day (react-datepicker uses the selected day)
  const minTime = useMemo(() => {
    const t = new Date(selected);
    if (isRange && rangeStart && sameDay(selected, rangeStart)) {
      const rs = new Date(rangeStart);
      const wh = new Date(selected);
      wh.setHours(whStartH, whStartM, 0, 0);
      return rs > wh ? rs : wh;
    }
    t.setHours(whStartH, whStartM, 0, 0);
    return t;
  }, [selected, isRange, rangeStart, whStartH, whStartM]);

  const maxTime = useMemo(() => {
    const t = new Date(selected);
    t.setHours(whEndH, whEndM, 0, 0);
    return t;
  }, [selected, whEndH, whEndM]);

  return (
    <DatePicker
      className="form-control"
      selected={selected}
      onChange={handleDateChange}
      // open/close control
      open={open}
      onInputClick={() => setOpen(true)}
      onClickOutside={() => setOpen(false)}
      shouldCloseOnSelect={true}
      // block manual typing
      readOnly
      onChangeRaw={(e) => e?.preventDefault?.()}
      onKeyDown={(e) => {
        if (e.key !== 'Tab') e.preventDefault();
      }}
      // date/time behavior
      filterDate={filterDate}
      showTimeSelect
      filterTime={filterTime}
      timeFormat="HH:mm"
      timeIntervals={stepMinutes}
      timeCaption="time"
      dateFormat="dd.MM.yyyy HH:mm"
      // bounds
      minDate={minSelectableDate}
      minTime={minTime}
      maxTime={maxTime}
      onFocus={(e) => e.target.click()}
    />
  );
}
